import { useEffect, useState } from "react";
import { CreditCard, Zap, TrendingUp, Package, FileText, AlertTriangle } from "lucide-react";
import { PageBody, PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { startCheckout } from "@/lib/checkout";
import { PLAN_LABEL } from "@/lib/constants";
import { toast } from "sonner";
import api from "@/lib/api";

const METER_LABEL = { contacts: "Contacts", sends: "Email sends", ai_credits: "AI Copilot credits", enrich_credits: "Enrichment credits" };

function Meter({ m }) {
  const pct = m.quota ? Math.min(100, Math.round((m.used / m.quota) * 100)) : 0;
  const over = m.quota && m.used >= m.quota;
  const near = pct >= 80;
  const color = over ? "bg-rose-500" : near ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid={`meter-${m.key}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-muted-foreground">{METER_LABEL[m.key] || m.key}</span>
        {over && <AlertTriangle className="h-4 w-4 text-rose-500" />}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="metric text-2xl">{m.used.toLocaleString()}</span>
        <span className="text-xs text-muted-foreground">/ {m.quota ? m.quota.toLocaleString() : "∞"}</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-accent overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
      {over && <p className="mt-2 text-xs text-rose-500">Quota reached - add a pack to keep going.</p>}
      {near && !over && <p className="mt-2 text-xs text-amber-500">{pct}% used</p>}
    </div>
  );
}

export default function Billing() {
  const [summary, setSummary] = useState(null);
  const [usage, setUsage] = useState(null);
  const [addons, setAddons] = useState([]);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/billing/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/plans").then((r) => setAddons(r.data.addons)).catch(() => {});
    api.get("/org/status").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const planId = summary?.organization?.plan_id;

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data.url) window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not open billing portal.");
    }
  };

  return (
    <PageBody>
      <PageHeader eyebrow="Billing & Usage" title="Plan, seats, meters & add-ons"
        subtitle="Your subscription funds the platform plus pooled Apollo credits. Hard stops or upsell when a quota is hit."
        actions={cfg?.billing_connected && cfg?.test_mode ? <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-500" data-testid="test-mode-badge">Test mode</Badge> : null} />

      {cfg && !cfg.billing_connected && <div data-testid="billing-not-connected" className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-500">Billing not connected. Add Stripe keys to enable checkout and the customer portal.</div>}
      {cfg && !cfg.mail_connected && <div data-testid="mail-not-connected" className="mb-6 rounded-lg border border-border/60 bg-card/50 px-4 py-3 text-sm text-muted-foreground">Mail not connected. Product emails are paused until Resend keys are set.</div>}
      {cfg?.billing_status === "past_due" && <div data-testid="billing-past-due" className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-500">Your last payment failed. Update billing within 3 days or live sending will pause. Your data stays safe.</div>}
      {cfg?.billing_status === "frozen" && <div data-testid="billing-frozen" className="mb-6 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-500">Live sending is paused due to a failed payment. Update billing to resume — nothing was deleted.</div>}


      <div className="grid lg:grid-cols-3 gap-5 mb-8">
        <div className="lg:col-span-2 rounded-xl border border-amber-500/40 bg-card/60 p-6 amber-glow">
          <div className="flex items-start justify-between">
            <div>
              <div className="eyebrow mb-2">Current plan</div>
              <div className="font-display text-2xl font-semibold">{PLAN_LABEL[planId] || "No active subscription"}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {summary?.organization?.seats ? `${summary.organization.seats} seats · ` : ""}{summary?.organization?.billing_cycle || (planId ? "monthly" : "start a plan to unlock quotas")}
              </div>
            </div>
            <CreditCard className="h-6 w-6 text-amber-500" />
          </div>
          <div className="mt-5 flex flex-wrap gap-2 items-center">
            {!planId && <Button onClick={() => startCheckout("professional_monthly")} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="subscribe-btn">Start Professional</Button>}
            {planId && planId !== "scale" && <Button onClick={() => startCheckout("scale_monthly")} className="rounded-md" variant="outline" data-testid="upgrade-btn">Upgrade to Scale</Button>}
            <div className="flex flex-col">
              <Button variant="outline" className="rounded-md" disabled={!cfg?.has_customer} onClick={openPortal} data-testid="manage-billing-btn">Manage billing</Button>
              {!cfg?.has_customer && <span className="text-[11px] text-amber-500 mt-1" data-testid="portal-not-connected">Billing not connected</span>}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-border/70 bg-card/50 p-6">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-emerald-500" /><span className="text-sm font-medium">Billing rules</span></div>
          <ul className="text-xs text-muted-foreground space-y-2">
            <li>Annual billing = 2 months free</li>
            <li>No free plan. No trial. Demo is scheduled.</li>
            <li>Usage meters with hard stops or upsell</li>
            <li>Cost per meeting shown on dashboard</li>
          </ul>
        </div>
      </div>

      <h3 className="font-display text-lg font-semibold tracking-tight mb-4 flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Usage meters</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {(usage?.meters || []).map((m) => <Meter key={m.key} m={m} />)}
      </div>

      <h3 className="font-display text-lg font-semibold tracking-tight mb-4 flex items-center gap-2"><Package className="h-4 w-4 text-amber-500" /> Add-ons</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {addons.map((a) => (
          <div key={a.lookup_key} className="rounded-xl border border-border/70 bg-card/50 p-5 flex items-center justify-between" data-testid={`addon-${a.lookup_key}`}>
            <div>
              <div className="text-sm font-medium">{a.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">+{a.credits.toLocaleString()} {a.kind}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => startCheckout(a.lookup_key)} className="rounded-md" data-testid={`buy-${a.lookup_key}`}>${a.price}</Button>
          </div>
        ))}
      </div>

      <h3 className="font-display text-lg font-semibold tracking-tight mb-4 flex items-center gap-2"><FileText className="h-4 w-4 text-amber-500" /> Invoices</h3>
      <div className="rounded-xl border border-border/70 overflow-hidden">
        {(summary?.invoices || []).length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No invoices yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border/70 bg-card/50 text-xs text-muted-foreground"><th className="text-left p-3 pl-4 font-medium">Item</th><th className="text-left p-3 font-medium">Date</th><th className="text-right p-3 pr-4 font-medium">Amount</th></tr></thead>
            <tbody>
              {summary.invoices.map((inv, i) => (
                <tr key={inv.id || `${inv.lookup_key}-${i}`} className="border-b border-border/40 last:border-0">
                  <td className="p-3 pl-4">{inv.lookup_key}</td>
                  <td className="p-3 text-muted-foreground">{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td className="p-3 pr-4 text-right metric">${inv.amount?.toFixed(2)} <Badge variant="outline" className="ml-2 text-[10px] text-emerald-500">paid</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </PageBody>
  );
}
